'use client';;
import { KEYS, TextApi, TrailingBlockPlugin } from 'platejs';
import { BaseSuggestionPlugin } from '@platejs/suggestion';
import { toTPlatePlugin } from 'platejs/react';

import {
  SuggestionLeaf,
  SuggestionLineBreak,
  VoidRemoveSuggestionOverlay,
} from '@/components/ui/suggestion-node';
import {
  discussionPlugin,
  getDiscussionBlockClickTarget,
  getDiscussionClickTarget,
} from './discussion-kit';

const INLINE_SUGGESTION_TARGET_PLUGINS = [
  KEYS.date,
  KEYS.inlineEquation,
  KEYS.link,
  KEYS.mention,
];

function getInlineSuggestionData(editor, element) {
  const suggestionApi = editor.getApi(BaseSuggestionPlugin).suggestion;
  const data = suggestionApi.suggestionData(element);

  if (data) return data;
  if (typeof suggestionApi.dataList !== 'function') return;

  for (const child of element.children) {
    if (!TextApi.isText(child)) continue;

    const childData = suggestionApi.dataList(child).at(-1);

    if (childData) return childData;
  }
}

export const suggestionPlugin = toTPlatePlugin(BaseSuggestionPlugin, ({ editor }) => ({
  options: {
    activeId: null,
    currentUserId: editor.getOption(discussionPlugin, 'currentUserId'),
    hoverId: null,
  },
})).configure({
  handlers: {
    // unset active suggestion when clicking outside of suggestion
    onClick: ({ api, event, setOption, type }) => {
      const markTarget = getDiscussionClickTarget({
        selector: `.slate-${type}`,
        target: event.target,
      });
      const blockTarget = markTarget
        ? null
        : getDiscussionBlockClickTarget({
            target: event.target,
          });

      if (!markTarget && !blockTarget) {
        setOption('activeId', null);
        return;
      }

      const suggestionEntry = api.suggestion?.node({
        isText: !blockTarget,
      });

      setOption('activeId', suggestionEntry
        ? (api.suggestion?.nodeId(suggestionEntry[0]) ?? null)
        : null);
    },
  },
  inject: {
    isElement: true,
    nodeProps: {
      nodeKey: '',
      styleKey: 'cssText',
      transformProps: ({ editor, element, props }) => {
        if (!element) return props;

        const suggestionData = getInlineSuggestionData(editor, element);

        if (!suggestionData) return props;

        return {
          ...props,
          'data-inline-suggestion': suggestionData.type,
        };
      },
      transformStyle: () => ({}),
    },
    targetPlugins: INLINE_SUGGESTION_TARGET_PLUGINS,
  },
  render: {
    belowNodes: SuggestionLineBreak,
    belowRootNodes: VoidRemoveSuggestionOverlay,
    node: SuggestionLeaf,
  },
});

const trailingBlockPlugin = TrailingBlockPlugin.configure({
  options: {
    insert: (editor, { insert }) => {
      editor.getApi(suggestionPlugin).suggestion.withoutSuggestions(insert);
    },
  },
});

export const SuggestionKit = [suggestionPlugin, trailingBlockPlugin];
